import {
    ChartBarIcon,
    ChartPieIcon,
    Cog6ToothIcon,
    CubeIcon,
    DocumentTextIcon,
    EnvelopeIcon,
    PhotoIcon,
    ShoppingBagIcon,
    TagIcon,
    TicketIcon,
    UsersIcon,
} from "@heroicons/react/24/outline";
import { motion, useDragControls } from "framer-motion";

const AdminSidebar = ({ activeTab, setActiveTab, mobileMenuOpen, setMobileMenuOpen }) => {
    const dragControls = useDragControls();

    // Handle swipe gestures for mobile
    const handleDragEnd = (event, info) => {
        const swipeThreshold = 100;
        const velocityThreshold = 500;

        // Swipe left to close sidebar (when open)
        if (mobileMenuOpen && (info.offset.x < -swipeThreshold || info.velocity.x < -velocityThreshold)) {
            setMobileMenuOpen(false);
        }
        // Swipe right to open sidebar (when closed) - handled in parent component
    };
    const menuItems = [
        {
            id: "dashboard",
            label: "Dashboard",
            icon: ChartBarIcon,
            description: "Overview & Analytics",
            gradient: "from-blue-500 to-cyan-500",
        },
        {
            id: "products",
            label: "Products",
            icon: CubeIcon,
            description: "Catalog Management",
            gradient: "from-green-500 to-emerald-500",
        },
        {
            id: "templates",
            label: "Templates",
            icon: DocumentTextIcon,
            description: "Design Library",
            gradient: "from-purple-500 to-violet-500",
        },
        {
            id: "users",
            label: "Users",
            icon: UsersIcon,
            description: "Customer Management",
            gradient: "from-orange-500 to-red-500",
        },
        {
            id: "orders",
            label: "Orders",
            icon: ShoppingBagIcon,
            description: "Order Processing",
            gradient: "from-indigo-500 to-blue-500",
        },
        {
            id: "categories",
            label: "Categories",
            icon: TagIcon,
            description: "Category Management",
            gradient: "from-pink-500 to-rose-500",
        },
        {
            id: "hot-selling",
            label: "Hot Selling",
            icon: TagIcon,
            description: "Manage Hot Selling Products",
            gradient: "from-red-500 to-pink-500",
        },
        {
            id: "festive-specials",
            label: "Festive Specials",
            icon: TagIcon,
            description: "Manage Festive Specials",
            gradient: "from-yellow-500 to-orange-500",
        },
        {
            id: "featured-templates",
            label: "Featured Templates",
            icon: DocumentTextIcon,
            description: "Manage Featured Templates",
            gradient: "from-purple-500 to-indigo-500",
        },
        {
            id: "inhouse-publications",
            label: "Inhouse Publications",
            icon: DocumentTextIcon,
            description: "Manage Inhouse Publications",
            gradient: "from-blue-500 to-cyan-500",
        },
        {
            id: "banners",
            label: "Banners",
            icon: PhotoIcon,
            description: "Manage Home & Category Banners",
            gradient: "from-purple-500 to-pink-500",
        },
        {
            id: "coupons",
            label: "Coupons",
            icon: TicketIcon,
            description: "Manage Discount Codes",
            gradient: "from-amber-500 to-yellow-500",
        },
        {
            id: "reports",
            label: "Reports",
            icon: ChartPieIcon,
            description: "Analytics & Insights",
            gradient: "from-teal-500 to-cyan-500",
        },
        {
            id: "settings",
            label: "Settings",
            icon: Cog6ToothIcon,
            description: "Site Status & Configuration",
            gradient: "from-gray-500 to-slate-500",
        },
        {
            id: "email-templates",
            label: "Email Templates",
            icon: EnvelopeIcon,
            description: "Manage Email Templates",
            gradient: "from-cyan-500 to-blue-500",
        },
    ];

    return (
        <>
            {/* Desktop Sidebar */}
            <div className="hidden lg:flex fixed left-0 top-0 h-full w-72 bg-white/95 backdrop-blur-xl border-r border-gray-200/50 shadow-xl z-50 flex-col">
                {/* Header Section */}
                <div className="p-6 border-b border-gray-100 flex-shrink-0">
                    <div className="flex items-center space-x-3">
                        <div className="relative">
                            <div className="w-10 h-10 bg-gradient-to-br from-[#008000] to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                                <span className="text-white font-bold text-lg">K</span>
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
                        </div>
                        <div>
                            <h2 className="text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                                Admin Portal
                            </h2>
                            <p className="text-sm text-gray-500">Kigo Prints</p>
                        </div>
                    </div>
                </div>

                {/* Navigation Menu */}
                <nav className="p-4 space-y-2 overflow-y-auto flex-1">
                    {menuItems.map((item, index) => {
                        const Icon = item.icon;
                        const isActive = activeTab === item.id;

                        return (
                            <motion.button
                                key={item.id}
                                onClick={() => setActiveTab(item.id)}
                                className={`group relative w-full flex items-center px-4 py-3.5 text-left rounded-2xl transition-all duration-300 ${isActive
                                    ? "bg-gradient-to-r from-green-50 to-emerald-50 text-[#008000] shadow-lg shadow-green-100/50"
                                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                    }`}
                                whileHover={{ x: isActive ? 0 : 4 }}
                                whileTap={{ scale: 0.98 }}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                            >
                                {/* Active Indicator */}
                                {isActive && (
                                    <motion.div
                                        className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-green-500 to-emerald-500 rounded-r-full"
                                        layoutId="activeIndicator"
                                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                    />
                                )}

                                {/* Icon Container */}
                                <div
                                    className={`relative p-2.5 rounded-xl transition-all duration-300 ${isActive
                                        ? `bg-gradient-to-br ${item.gradient} shadow-lg`
                                        : "bg-gray-100 group-hover:bg-gray-200"
                                        }`}
                                >
                                    <Icon
                                        className={`h-5 w-5 transition-colors duration-300 ${isActive ? "text-white" : "text-gray-600 group-hover:text-gray-700"
                                            }`}
                                    />
                                </div>

                                {/* Text Content */}
                                <div className="ml-3 flex-1 min-w-0">
                                    <p
                                        className={`font-semibold transition-colors duration-300 ${isActive ? "text-[#008000]" : "text-gray-700 group-hover:text-gray-900"
                                            }`}
                                    >
                                        {item.label}
                                    </p>
                                    <p
                                        className={`text-xs transition-colors duration-300 ${isActive ? "text-[#008000]" : "text-gray-500 group-hover:text-gray-600"
                                            }`}
                                    >
                                        {item.description}
                                    </p>
                                </div>

                                {/* Hover Effect */}
                                {!isActive && (
                                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-gray-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                )}
                            </motion.button>
                        );
                    })}
                </nav>

                {/* Bottom Section */}
                {/* <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-4 border border-green-100">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                                <span className="text-white text-sm font-bold">!</span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#008000]">Quick Tip</p>
                                <p className="text-xs text-[#008000] leading-relaxed">
                                    Use keyboard shortcuts to navigate faster
                                </p>
                            </div>
                        </div>
                    </div>
                </div> */}
            </div>

            {/* Mobile Sidebar */}
            <motion.div
                className={`lg:hidden fixed left-0 top-0 h-full w-80 max-w-[85vw] bg-white/98 backdrop-blur-xl border-r border-gray-200/50 shadow-2xl z-50 flex flex-col ${mobileMenuOpen ? "" : "pointer-events-none"
                    }`}
                initial={{ x: -320 }}
                animate={{ x: mobileMenuOpen ? 0 : -320 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                drag="x"
                dragControls={dragControls}
                dragConstraints={{ left: -320, right: 0 }}
                dragElastic={{ left: 0.1, right: 0.2 }}
                onDragEnd={handleDragEnd}
                whileDrag={{ scale: 0.98 }}
            >
                {/* Mobile Header */}
                <div className="p-6 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center space-x-3">
                        <div className="relative">
                            <div className="w-10 h-10 bg-gradient-to-br from-[#008000] to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                                <span className="text-white font-bold text-lg">K</span>
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
                        </div>
                        <div>
                            <h2 className="text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                                Admin Portal
                            </h2>
                            <p className="text-sm text-gray-500">Kigo Prints</p>
                        </div>
                    </div>

                    {/* Close Button */}
                    <button
                        onClick={() => setMobileMenuOpen(false)}
                        className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
                    >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {/* Mobile Navigation Menu */}
                <nav className="p-4 space-y-2 overflow-y-auto flex-1">
                    {menuItems.map((item, index) => {
                        const Icon = item.icon;
                        const isActive = activeTab === item.id;

                        return (
                            <motion.button
                                key={item.id}
                                onClick={() => {
                                    setActiveTab(item.id);
                                    setMobileMenuOpen(false);
                                }}
                                className={`group relative w-full flex items-center px-4 py-4 text-left rounded-2xl transition-all duration-300 ${isActive
                                    ? "bg-gradient-to-r from-green-50 to-emerald-50 text-[#008000] shadow-lg shadow-green-100/50"
                                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                    }`}
                                whileHover={{ x: isActive ? 0 : 6 }}
                                whileTap={{ scale: 0.98 }}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: mobileMenuOpen ? 1 : 0, x: mobileMenuOpen ? 0 : -20 }}
                                transition={{ duration: 0.3, delay: mobileMenuOpen ? index * 0.05 : 0 }}
                            >
                                {/* Active Indicator */}
                                {isActive && (
                                    <motion.div
                                        className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-10 bg-gradient-to-b from-green-500 to-emerald-500 rounded-r-full"
                                        layoutId="mobileActiveIndicator"
                                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                    />
                                )}

                                {/* Icon Container */}
                                <div
                                    className={`relative p-3 rounded-xl transition-all duration-300 ${isActive
                                        ? `bg-gradient-to-br ${item.gradient} shadow-lg`
                                        : "bg-gray-100 group-hover:bg-gray-200"
                                        }`}
                                >
                                    <Icon
                                        className={`h-6 w-6 transition-colors duration-300 ${isActive ? "text-white" : "text-gray-600 group-hover:text-gray-700"
                                            }`}
                                    />
                                </div>

                                {/* Text Content */}
                                <div className="ml-4 flex-1 min-w-0">
                                    <p
                                        className={`font-semibold text-base transition-colors duration-300 ${isActive ? "text-[#008000]" : "text-gray-700 group-hover:text-gray-900"
                                            }`}
                                    >
                                        {item.label}
                                    </p>
                                    <p
                                        className={`text-sm transition-colors duration-300 ${isActive ? "text-[#008000]" : "text-gray-500 group-hover:text-gray-600"
                                            }`}
                                    >
                                        {item.description}
                                    </p>
                                </div>

                                {/* Arrow Indicator */}
                                <div
                                    className={`transition-colors duration-300 ${isActive ? "text-[#008000]" : "text-gray-400 group-hover:text-gray-600"
                                        }`}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M9 5l7 7-7 7"
                                        />
                                    </svg>
                                </div>

                                {/* Hover Effect */}
                                {!isActive && (
                                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-gray-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                )}
                            </motion.button>
                        );
                    })}
                </nav>

                {/* Mobile Bottom Section */}
                {/* <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-4 border border-green-100">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                                <span className="text-white text-sm font-bold">!</span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#008000]">Quick Tip</p>
                                <p className="text-xs text-[#008000] leading-relaxed">Swipe right to open menu</p>
                            </div>
                        </div>
                    </div>
                </div> */}
            </motion.div>
        </>
    );
};

export default AdminSidebar;
